/**
 * FinOps Reality Check — public lead magnet.
 *
 * Flow:
 *   1. Visitor sees a 1-screen hero with "Take the 60-second FinOps Reality Check".
 *   2. Click "Start" → 5 questions, one at a time, with a progress bar.
 *   3. After Q5 → results page with:
 *        • Big score (0-100) + band (Pre-FinOps / Reactive / Disciplined / Optimized)
 *        • Per-axis bar chart
 *        • Top-3 pains with CARTIE engine recommendations
 *        • Email-capture for "Email me the full report"
 *        • Primary CTA to /pricing
 *
 * Backend: POST /api/finops-maturity/score (anonymous-OK; email optional)
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Sparkles, ArrowRight, Loader2, Gauge, Mail, Check, RotateCw, Trophy, AlertTriangle, ChevronRight, ChevronLeft } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BAND_STYLES = {
  'Pre-FinOps':    { ring: 'border-rose-500/40 bg-rose-500/10',    text: 'text-rose-200',    accent: 'text-rose-300',    icon: AlertTriangle },
  'Reactive':      { ring: 'border-amber-500/40 bg-amber-500/10',  text: 'text-amber-100',   accent: 'text-amber-300',   icon: Gauge },
  'Disciplined':   { ring: 'border-emerald-500/40 bg-emerald-500/10',    text: 'text-emerald-100',    accent: 'text-emerald-300',    icon: Sparkles },
  'Optimized':     { ring: 'border-emerald-500/40 bg-emerald-500/10', text: 'text-emerald-100', accent: 'text-emerald-300', icon: Trophy },
};

const FinOpsRealityCheck = () => {
  const [questions, setQuestions] = useState([]);
  const [stage, setStage] = useState('intro'); // intro | quiz | submitting | result
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({}); // {qid: optid}
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [role, setRole] = useState('');
  const [result, setResult] = useState(null);
  const [emailed, setEmailed] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/finops-maturity/questions`)
      .then(r => setQuestions(r.data.questions || []))
      .catch(() => setError('Could not load the quiz. Refresh the page.'));
  }, []);

  const submit = async (withEmail) => {
    setStage('submitting');
    setError('');
    try {
      const payload = {
        answers: Object.entries(answers).map(([qid, oid]) => ({ question_id: qid, option_id: oid })),
        ...(withEmail && email ? { email, company: company || undefined, role: role || undefined } : {}),
      };
      const { data } = await axios.post(`${API_URL}/finops-maturity/score`, payload);
      setResult(data);
      // Only show the success state if the backend confirms the email actually
      // sent (Resend returned an ID). Otherwise surface a graceful retry path
      // so we never tell the user "Sent!" when nothing landed in their inbox.
      if (withEmail && email) {
        if (data?.email_sent === true) {
          setEmailed(true);
        } else {
          setError("We couldn't send the email right now. Please try again in a moment — your score is still saved below.");
        }
      }
      setStage('result');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Something went wrong. Try again.');
      setStage('quiz');
    }
  };

  const handlePick = (qid, oid) => {
    const next = { ...answers, [qid]: oid };
    setAnswers(next);
    // Auto-advance after a beat for a smooth feel
    setTimeout(() => {
      if (idx < questions.length - 1) {
        setIdx(idx + 1);
      } else {
        // last question — submit anonymously immediately so user sees results,
        // then offer email follow-up after.
        submit(false);
      }
    }, 320);
  };

  // ----- INTRO -----
  const IntroScreen = () => (
    <div className="text-center max-w-2xl mx-auto" data-testid="quiz-intro">
      <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-900 text-amber-300 text-[11px] font-mono uppercase tracking-[0.18em] mb-6 shadow-lg shadow-slate-900/20 border border-slate-800">
        <Gauge className="w-3.5 h-3.5" /> Free · 60-second check · No signup
      </div>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-5 text-white leading-[1.05]">
        Take the{' '}
        <span className="text-emerald-400">FinOps Reality Check</span>
      </h1>
      <p className="text-lg text-slate-300 leading-relaxed mb-8 font-medium">
        5 questions. 60 seconds. You'll see exactly where you sit on the <span className="text-white font-semibold">0-100 FinOps reality curve</span> —
        and the top 3 cost-pain points draining the most money today, plus the CARTIEAI engine that fixes each.
      </p>
      <Button
        size="lg"
        onClick={() => setStage('quiz')}
        disabled={!questions.length}
        className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold px-8 h-14 text-base shadow-md shadow-emerald-500/20 transition-all hover:-translate-y-0.5"
        data-testid="quiz-start-btn"
      >
        {!questions.length ? (
          <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading…</>
        ) : (
          <>Start the reality check <ArrowRight className="w-4 h-4 ml-2" /></>
        )}
      </Button>
      <p className="text-xs text-slate-500 mt-6">
        Anonymous results count toward our public benchmark · Email-capture only after you see your score.
      </p>
    </div>
  );

  // ----- QUIZ -----
  const QuizScreen = () => {
    const q = questions[idx];
    if (!q) return null;
    const picked = answers[q.id];
    return (
      <div className="max-w-2xl mx-auto" data-testid="quiz-screen">
        {/* Progress */}
        <div className="mb-6 flex items-center justify-between">
          <div className="text-xs uppercase tracking-widest text-slate-400 font-mono">
            Question {idx + 1} of {questions.length} · {q.axis}
          </div>
          <button type="button" onClick={() => idx > 0 && setIdx(idx - 1)} disabled={idx === 0}
                  className="text-xs text-slate-500 hover:text-slate-400 disabled:opacity-30 inline-flex items-center gap-1"
                  data-testid="quiz-back-btn">
            <ChevronLeft className="w-3.5 h-3.5" /> back
          </button>
        </div>
        <div className="h-1 bg-white/5 rounded-full mb-8 overflow-hidden">
          <div className="h-full bg-emerald-400 bg-gradient-to-r from-emerald-400 to-teal-400 transition-all duration-500"
               style={{ width: `${((idx + (picked ? 1 : 0)) / questions.length) * 100}%` }} />
        </div>

        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-7 leading-snug">{q.prompt}</h2>

        <div className="space-y-2.5">
          {q.options.map((o) => {
            const isSelected = picked === o.id;
            return (
              <button
                key={o.id}
                type="button"
                onClick={() => handlePick(q.id, o.id)}
                disabled={!!picked}
                data-testid={`quiz-opt-${o.id}`}
                className={`w-full text-left px-5 py-4 rounded-xl border transition-all flex items-center justify-between gap-4 group ${
                  isSelected
                    ? 'border-emerald-400/70 bg-emerald-500/15 text-white shadow-lg shadow-emerald-500/20'
                    : 'border-white/10 bg-slate-900/40 text-slate-200 hover:border-emerald-400/40 hover:bg-emerald-500/[0.05]'
                }`}
              >
                <span className="text-sm sm:text-base font-medium">{o.label}</span>
                <span className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${
                  isSelected ? 'border-emerald-400 bg-emerald-500' : 'border-slate-600 group-hover:border-emerald-400'
                }`}>
                  {isSelected ? <Check className="w-3.5 h-3.5 text-slate-950" /> : null}
                </span>
              </button>
            );
          })}
        </div>
        {error && <div className="mt-4 text-sm text-rose-300">{error}</div>}
      </div>
    );
  };

  // ----- RESULTS -----
  const ResultScreen = () => {
    if (!result) return null;
    const bandStyle = BAND_STYLES[result.band] || BAND_STYLES['Reactive'];
    const Icon = bandStyle.icon;
    return (
      <div className="max-w-3xl mx-auto" data-testid="quiz-result">
        {/* Score card */}
        <div className={`rounded-2xl border-2 ${bandStyle.ring} p-8 sm:p-10 text-center mb-8`}>
          <div className="inline-flex items-center gap-2 mb-3">
            <Icon className={`w-4 h-4 ${bandStyle.accent}`} />
            <span className={`text-xs uppercase tracking-widest font-bold ${bandStyle.accent}`}>{result.band}</span>
          </div>
          <div className={`text-6xl sm:text-7xl font-extrabold ${bandStyle.text} tabular-nums leading-none`} data-testid="quiz-result-score">
            {result.score}
            <span className={`text-2xl sm:text-3xl ${bandStyle.accent}`}>/100</span>
          </div>
          <p className={`mt-5 text-base sm:text-lg ${bandStyle.text} leading-relaxed max-w-xl mx-auto`}>
            {result.band_blurb}
          </p>
        </div>

        {/* Per-axis breakdown */}
        <div className="rounded-xl bg-slate-900/50 border border-white/10 p-6 mb-8">
          <h3 className="text-sm uppercase tracking-widest text-slate-400 font-mono mb-4">By axis</h3>
          <div className="space-y-3">
            {result.per_axis.map((a) => (
              <div key={a.axis}>
                <div className="flex items-center justify-between text-sm mb-1.5">
                  <span className="text-white">{a.axis}</span>
                  <span className="text-slate-400 font-mono">{a.score} / {a.max}</span>
                </div>
                <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      a.score >= 18 ? 'bg-emerald-400' : a.score >= 10 ? 'bg-cyan-400' : a.score >= 5 ? 'bg-amber-400' : 'bg-rose-400'
                    }`}
                    style={{ width: `${(a.score / a.max) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top 3 pains + CARTIE engines */}
        {result.top_pains && result.top_pains.length > 0 && (
          <div className="rounded-xl bg-slate-900/50 border border-emerald-500/20 p-6 mb-8" data-testid="quiz-top-pains">
            <h3 className="text-sm uppercase tracking-widest text-emerald-300 font-mono mb-4">
              Your top 3 cost-pain points · and what fixes each
            </h3>
            <div className="space-y-3">
              {result.top_pains.map((p, i) => (
                <div key={p.pain_id} className="flex items-start gap-4 p-4 rounded-lg bg-white/[0.02] border border-white/5">
                  <div className="text-3xl font-extrabold text-emerald-400/70 tabular-nums shrink-0">#{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-base font-semibold text-white mb-0.5">{p.name}</div>
                    <div className="text-xs text-slate-400">
                      CARTIE engine: <span className="text-emerald-300 font-mono">{p.cartie_engine}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Email capture & CTA */}
        {!emailed ? (
          <div className="rounded-xl bg-gradient-to-br from-emerald-950/40 to-slate-900/40 border border-emerald-500/30 p-6 sm:p-8 mb-6" data-testid="quiz-email-capture">
            <div className="flex items-center gap-3 mb-3">
              <Mail className="w-5 h-5 text-emerald-300" />
              <h3 className="text-lg font-bold text-white">Email me the full report</h3>
            </div>
            <p className="text-sm text-slate-400 mb-5 leading-relaxed">
              Get the detailed write-up — including the specific CARTIE engines mapped to your top pains, a comparison vs Vantage / CloudHealth / Datadog, and a 30-day pilot plan tuned to your maturity band.
            </p>
            <div className="grid sm:grid-cols-2 gap-3 mb-3">
              <Input
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-slate-900/60 border-white/10 text-white"
                data-testid="quiz-email-input"
              />
              <Input
                type="text"
                placeholder="Company (optional)"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                className="bg-slate-900/60 border-white/10 text-white"
                data-testid="quiz-company-input"
              />
            </div>
            <Input
              type="text"
              placeholder="Your role (optional · e.g. VP Eng, FinOps Lead)"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="bg-slate-900/60 border-white/10 text-white mb-4"
              data-testid="quiz-role-input"
            />
            <Button
              onClick={() => submit(true)}
              disabled={!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)}
              className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold w-full sm:w-auto"
              data-testid="quiz-email-submit"
            >
              <Mail className="w-4 h-4 mr-2" />Email me the report
            </Button>
            <p className="text-[11px] text-slate-500 mt-3 italic">
              No spam. One email. Unsubscribe anytime. We don't sell or share addresses.
            </p>
          </div>
        ) : (
          <div className="rounded-xl bg-emerald-500/10 border border-emerald-400/40 p-6 text-center mb-6" data-testid="quiz-email-success">
            <Check className="w-6 h-6 text-emerald-300 mx-auto mb-2" />
            <div className="text-base font-semibold text-emerald-100">Sent! Check your inbox.</div>
            <div className="text-xs text-emerald-200/70 mt-1">
              We just emailed your detailed report to <span className="font-mono">{email}</span>.
            </div>
            <div className="text-[11px] text-emerald-200/60 mt-2 italic">
              Didn&apos;t arrive in 2 minutes? Check your spam / promotions folder — it comes from <span className="font-mono">hello@cartieai.com</span>.
            </div>
          </div>
        )}

        {/* Final CTAs */}
        <div className="flex flex-col sm:flex-row flex-wrap gap-3 justify-center">
          <Link to="/pricing">
            <Button size="lg" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold px-6 h-12 w-full sm:w-auto shadow-md shadow-emerald-500/20 transition-all hover:-translate-y-0.5" data-testid="quiz-cta-pilot">
              <Sparkles className="w-4 h-4 mr-2" />Start a 7-day pilot
            </Button>
          </Link>
          <Link to="/tools/ai-cost-calculator">
            <Button size="lg" variant="outline" className="border-emerald-400/40 bg-emerald-500/5 text-emerald-200 hover:bg-emerald-500/15 hover:text-white px-6 h-12 w-full sm:w-auto" data-testid="quiz-cta-ai-calc">
              Try AI Cost Calculator <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
          <Button
            size="lg" variant="outline"
            onClick={() => { setStage('intro'); setIdx(0); setAnswers({}); setResult(null); setEmailed(false); setEmail(''); setCompany(''); setRole(''); }}
            className="border-white/15 bg-white/5 text-slate-200 hover:bg-white/10 hover:text-white px-6 h-12 w-full sm:w-auto"
            data-testid="quiz-retake-btn"
          >
            <RotateCw className="w-4 h-4 mr-2" />Retake the test
          </Button>
          <Link to="/">
            <Button size="lg" variant="outline" className="border-white/10 bg-transparent text-slate-400 hover:bg-white/5 hover:text-white px-6 h-12 w-full sm:w-auto" data-testid="quiz-cta-home">
              ← Back to home
            </Button>
          </Link>
        </div>
      </div>
    );
  };

  return (
    <div className="dark min-h-screen bg-slate-950 text-slate-100 relative overflow-hidden">
      <Helmet>
        <title>FinOps Reality Check · CARTIEAI</title>
        <meta name="description" content="60-second FinOps reality check. Score your cloud cost discipline 0-100 and find the top 3 pains draining your bill." />
      </Helmet>

      {/* Subtle background — radial emerald glow + grid */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(16,185,129,0.10),transparent_55%)] pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_70%,rgba(6,182,212,0.06),transparent_55%)] pointer-events-none" />

      <div className="relative max-w-5xl mx-auto px-4 pt-24 pb-20">
        {stage === 'intro' && <IntroScreen />}
        {stage === 'quiz' && <QuizScreen />}
        {stage === 'submitting' && (
          <div className="text-center py-32" data-testid="quiz-submitting">
            <Loader2 className="w-10 h-10 text-emerald-600 animate-spin mx-auto mb-4" />
            <div className="text-lg text-slate-300 font-medium">Scoring your answers…</div>
          </div>
        )}
        {stage === 'result' && <ResultScreen />}
      </div>
    </div>
  );
};

export default FinOpsRealityCheck;
